import { currencyFormatter } from "@fla/common";
import {
  AdditionalRequirementsPaymentProps,
  AssetType,
  Bank_InitialState,
  Business_InitialState,
  ConsentOrdersPathName,
  DebtType,
  Debt_InitialState,
  FormNavigation,
  Furniture_InitialState,
  Investments_InitialState,
  OverseasAsset_InitialState,
  RealEstate_InitialState,
  Superannuation_InitialState,
  Vehicle_InitialState,
  toast,
  useBank,
  useBusiness,
  useDebts,
  useFurniture,
  useInvestments,
  useNames,
  useOptions,
  useOverseas,
  useRealEstate,
  useSuper,
  useVehicle
} from "@fla/components";
import ListIcon from "@fla/components/src/assets/svgs/list.svg";
import { useAppDispatch } from "app/store";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { selectAsset, selectBaseAssetsDebts, selectConsentOrdersBase, selectDebt } from "pages/ConsentOrders";
import { consentOrdersActions } from "pages/ConsentOrders/consentOrdersSlice";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const AdditionalRequirementsCard = ({
  idx,
  data,
  getName
}: {
  getName: (val?: string) => any;
  idx: number;
  data: Partial<AdditionalRequirementsPaymentProps>;
}) => {
  return (
    <div key={idx} className="rounded-xl shadow-md bg-white p-5 mb-5 grid grid-cols-8 gap-y-4 md:gap-y-0">
      <div className="col-span-2 md:col-span-1 flex items-center">
        <div className="w-12 h-12 bg-gray-300 rounded-full flex justify-center items-center text-gray-500 text-2xl">
          $
        </div>
      </div>
      <div className="col-span-6 md:col-span-5 pr-5">
        <p className="text-lg font-medium">
          Payment from {getName(data.payor)} to&nbsp;
          {getName(data.payee) || data.other_person_entity}
        </p>
        <p className="text-sm font-light line-clamp-3">{data.description}</p>
      </div>
      <div className="col-span-8 md:col-span-2 flex justify-end">
        <p className="text-xl">{currencyFormatter.format(Number(data.value))}</p>
      </div>
    </div>
  );
};

export default function Summary() {
  const [status, setStatus] = useState(SliceStatus.Idle);
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const _useNames = useNames({ initialDetails });
  const _useOptions = useOptions({ initialDetails });
  const { spouseFirstName, userFirstName, getName } = _useNames;
  const assetsDebts = useSelector(selectBaseAssetsDebts);
  const { updateAssetsDebts } = useConsentOrders();
  const navigation = useNavigate();
  const dispatch = useAppDispatch();

  const userDebts =
    Array.isArray(assetsDebts?.additional_requirements.data.payments) &&
    assetsDebts?.additional_requirements.data.payments.length
      ? assetsDebts?.additional_requirements.data.payments.filter(data => data.payor === "user")
      : [];
  const userAssets =
    Array.isArray(assetsDebts?.additional_requirements.data.payments) &&
    assetsDebts?.additional_requirements.data.payments.length
      ? assetsDebts?.additional_requirements.data.payments.filter(data => data.payee === "user")
      : [];

  const spouseDebts =
    Array.isArray(assetsDebts?.additional_requirements.data.payments) &&
    assetsDebts?.additional_requirements.data.payments.length
      ? assetsDebts?.additional_requirements.data.payments.filter(data => data.payor === "spouse")
      : [];
  const spouseAssets =
    Array.isArray(assetsDebts?.additional_requirements.data.payments) &&
    assetsDebts?.additional_requirements.data.payments.length
      ? assetsDebts?.additional_requirements.data.payments.filter(data => data.payee === "spouse")
      : [];

  const realEstateData = useSelector(selectAsset(AssetType.REAL_ESTATE)) as RealEstate_InitialState[];
  const superannuationData = useSelector(selectAsset(AssetType.SUPERANNUATION)) as Superannuation_InitialState[];
  const bankData = useSelector(selectAsset(AssetType.BANK)) as Bank_InitialState[];
  const vehicleData = useSelector(selectAsset(AssetType.VEHICLE)) as Vehicle_InitialState[];
  const investmentsData = useSelector(selectAsset(AssetType.INVESTMENTS)) as Investments_InitialState[];
  const businessData = useSelector(selectAsset(AssetType.BUSINESS)) as Business_InitialState[];
  const overseasData = useSelector(selectAsset(AssetType.OVERSEAS)) as OverseasAsset_InitialState[];
  const furnitureAndPersonalData = useSelector(
    selectAsset(AssetType.FURNITURE_AND_PERSONAL)
  ) as Furniture_InitialState[];
  const debtsData = useSelector(selectDebt(DebtType.DEBTS)) as Debt_InitialState[];

  const {
    sellCard: getRealSellCard,
    keepCard: getRealKeepCard,
    paymentReceivingFromCard: getRealKeeperPayingToCard,
    payingToCard: getRealPayingToCard,
    userTotalAmount: realUserTotalAmount,
    spouseTotalAmount: realSpouseTotalAmount
  } = useRealEstate({
    realEstateData,
    ..._useNames
  });
  const {
    getCardFor: getSuperCardFor,
    userTotalAmount: superUserTotalAmount,
    spouseTotalAmount: superSpouseTotalAmount
  } = useSuper({
    superannuationData,
    ..._useNames
  });
  const {
    ownCard: bankOwnCard,
    splitCard: bankSplitCard,
    userTotalAmount: bankUserTotalAmount,
    spouseTotalAmount: bankSpouseTotalAmount
  } = useBank({
    bankData,
    ..._useNames
  });
  const {
    ownCard: vehicleOwnCard,
    splitCard: vehicleSplitCard,
    userTotalAmount: vehicleUserTotalAmount,
    spouseTotalAmount: vehicleSpouseTotalAmount
  } = useVehicle({
    vehicleData,
    ..._useNames
  });
  const {
    ownCard: investmentsOwnCard,
    splitCard: investmentsSplitCard,
    userTotalAmount: investmentsUserTotalAmount,
    spouseTotalAmount: investmentsSpouseTotalAmount
  } = useInvestments({
    investmentsData,
    ..._useNames
  });

  const {
    ownCard: businessOwnCard,
    splitCard: businessSplitCard,
    userTotalAmount: businessUserTotalAmount,
    spouseTotalAmount: businessSpouseTotalAmount
  } = useBusiness({
    businessData,
    ..._useNames
  });
  const {
    ownCard: overseasOwnCard,
    splitCard: overseasSplitCard,
    userTotalAmount: overseasUserTotalAmount,
    spouseTotalAmount: overseasSpouseTotalAmount
  } = useOverseas({
    overseasData,
    ..._useNames,
    ..._useOptions
  });

  const {
    ownCard: furnitureOwnCard,
    splitCard: furnitureSplitCard,
    valueAllocatedToCard: furnitureValueAllocatedToCard,
    userTotalAmount: furnitureUserTotalAmount,
    spouseTotalAmount: furnitureSpouseTotalAmount
  } = useFurniture({
    furnitureAndPersonalData,
    assetsDebts,
    ..._useNames
  });

  const {
    ownCard: debtsOwnCard,
    splitCard: debtsSplitCard,
    userTotalAmount: debtsUserTotalAmount,
    spouseTotalAmount: debtsSpouseTotalAmount
  } = useDebts({
    debtsData,
    ..._useNames
  });

  const userTotalAssets =
    realUserTotalAmount +
    superUserTotalAmount +
    bankUserTotalAmount +
    vehicleUserTotalAmount +
    investmentsUserTotalAmount +
    businessUserTotalAmount +
    overseasUserTotalAmount +
    furnitureUserTotalAmount +
    userAssets.reduce((p, c) => p + Number(c.value), 0) + // in additional requirements if payor=user then add payments value
    Number(assetsDebts?.furniture?.data?.user_value ?? 0); //allocated furniture to user value

  const userTotalDebts = debtsUserTotalAmount + userDebts.reduce((p, c) => p + Number(c.value), 0); //in additional requirements if payor=user then add payments value

  const spouseTotalAssets =
    realSpouseTotalAmount +
    superSpouseTotalAmount +
    bankSpouseTotalAmount +
    vehicleSpouseTotalAmount +
    investmentsSpouseTotalAmount +
    businessSpouseTotalAmount +
    overseasSpouseTotalAmount +
    furnitureSpouseTotalAmount +
    spouseAssets.reduce((p, c) => p + Number(c.value), 0) + // in additional requirements if payee=spouse then add payments value
    Number(assetsDebts?.furniture?.data?.spouse_value ?? 0); //allocated furniture to spouse value

  const spouseTotalDebts = debtsSpouseTotalAmount + spouseDebts.reduce((p, c) => p + Number(c.value), 0); // in additional requirements if payor=spouse then add payments value

  const totalEquity = userTotalAssets - userTotalDebts + (spouseTotalAssets - spouseTotalDebts);
  const userEquity = userTotalAssets - userTotalDebts;
  const spouseEquity = spouseTotalAssets - spouseTotalDebts;

  const userEquityPercentage = (userEquity * 100) / totalEquity;
  const spouseEquityPercentage = (spouseEquity * 100) / totalEquity;

  const onContinue = async (e: any) => {
    e.preventDefault();
    try {
      setStatus(SliceStatus.Pending);

      const payload = {
        summary: { isCompleted: true },
        currentStep: 11
      };
      await updateAssetsDebts(payload);
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      setStatus(SliceStatus.Resolved);
      navigation(ConsentOrdersPathName.Submit);
    } catch (error: any) {
      setStatus(SliceStatus.Rejected);

      toast({
        type: "error",
        message: error?.message || "something went wrong, please try again!"
      });
    }
  };

  const handleBackClick = () => {
    navigation(-1);
  };

  return (
    <section className="w-full px-5 md:px-0 py-10">
      <div>
        <img src={ListIcon} className="w-10 h-auto mx-auto my-3" alt="summary" />
        <h1 className="text-center mb-16">Summary</h1>
      </div>
      <form id="summary-form" onSubmit={onContinue}>
        {/* ---------------------User Assets/Debts------------------- */}
        <section className="py-10">
          <div className="rounded-xl md:w-10/12 lg:max-w-2xl mx-auto h-fit space-y-10">
            <div className="w-full space-y-5">
              <p className="font-medium text-xl text-gray">{userFirstName}'s Assets</p>

              {/* -----real-estate---- */}
              {getRealSellCard("user")}
              {getRealKeepCard("user")}
              {/* spouse paying to user */}
              {getRealKeeperPayingToCard("spouse")}
              {/* ----end real-estate --- */}

              {/* -----super---- */}
              {getSuperCardFor("user")}
              {/* ----end super --- */}

              {/* -----bank---- */}
              {bankOwnCard("user")}
              {bankSplitCard("user")}
              {/* ----end bank --- */}

              {/* -----vehicle---- */}
              {vehicleOwnCard("user")}
              {vehicleSplitCard("user")}
              {/* ----end vehicle --- */}

              {/* -----investments---- */}
              {investmentsOwnCard("user")}
              {investmentsSplitCard("user")}
              {/* ----end investments --- */}

              {/* -----business---- */}
              {businessOwnCard("user")}
              {businessSplitCard("user")}
              {/* ----end business --- */}

              {/* -----overseas---- */}
              {overseasOwnCard("user")}
              {overseasSplitCard("user")}
              {/* ----end overseas --- */}

              {/* -----furniture---- */}
              {furnitureOwnCard("user")}
              {furnitureSplitCard("user")}
              {furnitureValueAllocatedToCard("user")}
              {/* ----end furniture --- */}

              {/* -----additional requirements assets---- */}
              {userAssets.map((data, idx) => (
                <AdditionalRequirementsCard key={+idx} idx={idx} data={data} getName={getName} />
              ))}
              {/* ----end additional requirements assets --- */}

              <div className="flex justify-end">
                <p className="text-gray-500">
                  TOTAL ASSETS:&nbsp;
                  <span className="font-medium text-xl">{currencyFormatter.format(userTotalAssets)}</span>
                </p>
              </div>
            </div>
            <hr className="my-10 h-0.5 bg-gray-300 max-w-6xl mx-auto" />
            <div className="w-full space-y-5">
              <p className="font-medium text-xl text-gray mb-7">{userFirstName}'s Debts</p>

              {/* -----debts---- */}
              {debtsOwnCard("user")}
              {debtsSplitCard("user")}
              {getRealPayingToCard("spouse", "")}
              {/* ----end debts --- */}

              {/* -----additional requirements debts---- */}
              {userDebts.map((data, idx) => (
                <AdditionalRequirementsCard key={+idx} idx={idx} data={data} getName={getName} />
              ))}
              {/* ----end additional requirements debts --- */}
              <div className="flex justify-end">
                <p className="text-gray-500 float-right">
                  TOTAL DEBTS:&nbsp;
                  <span className="font-medium text-xl">{currencyFormatter.format(userTotalDebts)}</span>
                </p>
              </div>
            </div>

            <div className="w-full">
              <div className="border-2 border-primary-faded p-4 rounded-lg flex justify-between">
                <p>
                  <span className="font-medium">{userFirstName}'s</span> Net Position:
                </p>
                <p>{currencyFormatter.format(userEquity)}</p>
              </div>
              <p className="font-light italic text-center my-4">These are approximate values</p>
            </div>
          </div>
        </section>

        {/* ---------------------Spouse Assets/Debts------------------- */}
        <section className="bg-gray-200/50 py-10">
          <div className="rounded-xl md:w-10/12 lg:max-w-2xl mx-auto h-fit space-y-10">
            <div className="w-full space-y-5">
              <p className="font-medium text-xl text-gray mb-7">{spouseFirstName}'s Assets</p>

              {/* -----real-estate---- */}
              {getRealSellCard("spouse")}
              {getRealKeepCard("spouse")}
              {/* user paying to spouse */}
              {getRealKeeperPayingToCard("user")}
              {/* ----end real-estate --- */}

              {/* -----super---- */}
              {getSuperCardFor("spouse")}
              {/* ----end super --- */}

              {/* -----bank---- */}
              {bankOwnCard("spouse")}
              {bankSplitCard("spouse")}
              {/* ----end bank --- */}

              {/* -----vehicle---- */}
              {vehicleOwnCard("spouse")}
              {vehicleSplitCard("spouse")}
              {/* ----end vehicle --- */}

              {/* -----investments---- */}
              {investmentsOwnCard("spouse")}
              {investmentsSplitCard("spouse")}
              {/* ----end investments --- */}

              {/* -----business---- */}
              {businessOwnCard("spouse")}
              {businessSplitCard("spouse")}
              {/* ----end business --- */}

              {/* -----overseas---- */}
              {overseasOwnCard("spouse")}
              {overseasSplitCard("spouse")}
              {/* ----end overseas --- */}

              {/* -----furniture---- */}
              {furnitureOwnCard("spouse")}
              {furnitureSplitCard("spouse")}
              {furnitureValueAllocatedToCard("spouse")}
              {/* ----end furniture --- */}

              {/* -----additional requirements assets---- */}
              {spouseAssets.map((data, idx) => (
                <AdditionalRequirementsCard key={idx} idx={idx} data={data} getName={getName} />
              ))}
              {/* ----end additional requirements assets --- */}

              <div className="flex justify-end">
                <p className="text-gray-500">
                  TOTAL ASSETS:&nbsp;
                  <span className="font-medium text-xl">{currencyFormatter.format(spouseTotalAssets)}</span>
                </p>
              </div>
            </div>

            <div className="w-full space-y-5">
              <p className="font-medium text-xl text-gray mb-7">{spouseFirstName}'s Debts</p>

              {/* -----debts---- */}
              {debtsOwnCard("spouse")}
              {debtsSplitCard("spouse")}
              {getRealPayingToCard("user", "")}

              {/* ----end debts --- */}

              {/* -----additional requirements debts---- */}
              {spouseDebts.map((data, idx) => (
                <AdditionalRequirementsCard key={idx} idx={idx} data={data} getName={getName} />
              ))}
              {/* ----end additional requirements debts --- */}

              <div className="flex justify-end">
                <p className="text-gray-500">
                  TOTAL DEBTS:&nbsp;
                  <span className="font-medium text-xl">{currencyFormatter.format(spouseTotalDebts)}</span>
                </p>
              </div>
            </div>

            <div className="w-full">
              <div className="border-2 border-primary-faded p-4 rounded-lg flex justify-between">
                <p>
                  <span className="font-medium">{spouseFirstName}'s</span> Net Position:
                </p>
                <p>{currencyFormatter.format(spouseEquity)}</p>
              </div>
              <p className="font-light italic text-center my-4">These are approximate values</p>
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="rounded-xl md:w-10/12 lg:max-w-2xl mx-auto h-fit space-y-10">
            {/* OVERVIEW */}
            {assetsDebts?.additional_requirements?.data?.details ? (
              <section className="space-y-5">
                <h5 className="text-gray">Additional Comments</h5>
                <div className="bg-white rounded-xl w-full px-10 py-5 shadow-md">
                  <p>{assetsDebts?.additional_requirements?.data?.details}</p>
                </div>
              </section>
            ) : null}

            {/* percentage overview */}
            <section className="space-y-5">
              <div>
                <h5 className="text-gray">Overview</h5>
                <p className="font-medium text-gray text-lg">Percentage Division of Total Assets</p>
              </div>
              <div className="border-2 border-primary-faded rounded-lg flex justify-between items-center p-4 text-lg relative">
                <div>
                  <p>
                    <span className="font-semibold text-xl">{userEquityPercentage.toFixed(2)}%</span>
                    &nbsp;&nbsp;
                    {userFirstName}
                  </p>
                </div>
                <div>
                  <p>
                    {spouseFirstName}&nbsp;&nbsp;
                    <span className="font-semibold text-xl">{spouseEquityPercentage.toFixed(2)}%</span>
                  </p>
                </div>

                {/* progress percentage */}
                <div
                  className="bg-primary-faded bg-opacity-25 absolute top-0 left-0 h-full"
                  style={{ width: `${userEquityPercentage.toFixed(2)}%` }}
                />
              </div>
            </section>

            {/* TOTAL ASSETS */}
            <section className="space-y-5">
              <h5 className="text-gray">Assets</h5>
              <section className="bg-white rounded-lg p-4 text-lg relative shadow-md">
                <div className="flex justify-between">
                  <p>
                    <span className="font-medium text-xl">{userFirstName}'s</span>
                    &nbsp;&nbsp; Assets
                  </p>
                  <p>{currencyFormatter.format(userTotalAssets)}</p>
                </div>
                <div className="flex justify-between">
                  <p>
                    <span className="font-medium text-xl">{spouseFirstName}'s</span>
                    &nbsp;&nbsp; Assets
                  </p>
                  <p>{currencyFormatter.format(spouseTotalAssets)}</p>
                </div>
              </section>

              <div className="border-2 border-primary-faded rounded-lg flex justify-between items-center p-4 text-xl font-medium relative">
                <p>Total Assets</p>
                <p>{currencyFormatter.format(userTotalAssets + spouseTotalAssets)}</p>
              </div>
            </section>

            {/* TOTAL DEBTS */}
            <section className="space-y-5">
              <h5 className="text-gray">Debts</h5>
              <div className="bg-white rounded-lg p-4 text-lg relative shadow-md">
                <div className="flex justify-between">
                  <p>
                    <span className="font-medium text-xl">{userFirstName}'s</span>
                    &nbsp; Debts
                  </p>
                  <p>{currencyFormatter.format(userTotalDebts)}</p>
                </div>
                <div className="flex justify-between">
                  <p>
                    <span className="font-medium text-xl">{spouseFirstName}'s</span>
                    &nbsp; Debts
                  </p>
                  <p>{currencyFormatter.format(spouseTotalDebts)}</p>
                </div>
              </div>

              <div className="border-2 border-primary-faded rounded-lg flex justify-between items-center p-4 text-xl font-medium relative">
                <p>Total Debts</p>
                <p>{currencyFormatter.format(userTotalDebts + spouseTotalDebts)}</p>
              </div>
            </section>

            {/* NET POSITION */}
            <section className="space-y-5">
              <h5 className="text-gray">Net Position</h5>
              <section className="bg-white rounded-lg p-4 text-lg relative shadow-md">
                <div className="flex justify-between">
                  <p>
                    <span className="font-medium text-xl">{userFirstName}'s</span>
                    &nbsp; Net
                  </p>
                  <p>{currencyFormatter.format(userEquity)}</p>
                </div>
                <div className="flex justify-between">
                  <p>
                    <span className="font-medium text-xl">{spouseFirstName}'s</span>
                    &nbsp; Net
                  </p>
                  <p>{currencyFormatter.format(spouseEquity)}</p>
                </div>
              </section>

              <div className="border-2 border-primary-faded rounded-lg flex justify-between items-center p-4 text-xl font-medium relative">
                <p>Total Net</p>
                <p>{currencyFormatter.format(userEquity + spouseEquity)}</p>
              </div>
            </section>
          </div>
        </section>
      </form>

      <FormNavigation
        isFirst={false}
        isLast={true}
        form="summary-form"
        handleBackClick={handleBackClick}
        isSubmitting={status === SliceStatus.Pending}
        lastButtonText="Continue"
        classes="my-12 max-w-2xl mx-auto"
      />
    </section>
  );
}
